import { useCallback, useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * useRazorpayCheckout — reusable hook that owns the whole "click → pay → verify"
 * flow for Standard Checkout.
 *
 * Steps executed inside `open()`:
 *   1. Ensure the checkout.js script is loaded (idempotent; adds a <script>
 *      tag exactly once per page load).
 *   2. Ask the server to create a Razorpay Order for the chosen plan.
 *      The server rejects non-owners and non-existent plans up-front.
 *   3. Open the Razorpay modal with the returned order id + amount + key.
 *   4. On success, POST the (order_id, payment_id, signature) triple back
 *      to the server for HMAC verification. Only when *that* returns 200
 *      do we consider the plan actually active.
 *   5. On failure / dismiss / signature failure, surface a friendly toast.
 *
 * The hook exposes:
 *   - `open({ plan })` — kicks off the flow.
 *   - `loading` — true between "click" and "Razorpay modal open" (so the
 *     caller can disable their button and avoid double-taps).
 */
const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && window.Razorpay) {
      resolve(true);
      return;
    }
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export function useRazorpayCheckout({ onSuccess } = {}) {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);

  // Prefetch price + key config so the caller can render the button label
  // ("Upgrade to Premium — ₹2499") without an extra round-trip on click.
  useEffect(() => {
    let cancelled = false;
    api.get("/razorpay/config")
      .then((r) => { if (!cancelled) setConfig(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const open = useCallback(async ({ plan, prefill = {} }) => {
    if (!plan) return;
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        throw new Error("Could not load payment gateway. Please check your connection and try again.");
      }

      const { data: order } = await api.post("/razorpay/create-order", { plan });
      if (!order?.order_id) throw new Error("Failed to create payment order.");

      // Open the Razorpay modal. The `handler` callback runs on successful
      // client-side capture — we then MUST verify server-side before trusting.
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Pune Spa Guide",
        description: `${order.plan === "premium" ? "Premium" : "Featured"} Plan — 30 days`,
        order_id: order.order_id,
        // Ensures the modal picks a sensible default even when the caller
        // didn't pass a prefill. Users can still change these inside the modal.
        prefill: {
          name: prefill.name || "",
          email: prefill.email || "",
          contact: prefill.contact || "",
        },
        theme: { color: "#1F4E3D" },   // matches brand accent
        modal: {
          ondismiss: () => {
            toast.message("Payment cancelled", { description: "You can retry the payment from your dashboard anytime." });
          },
        },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/razorpay/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan: order.plan,
            });
            toast.success("Payment successful! Your plan is now active.");
            if (typeof onSuccess === "function") onSuccess(data);
          } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "Payment verification failed.";
            toast.error(detail);
          }
        },
      });

      // `payment.failed` fires when the gateway itself rejects the payment
      // (e.g. bank declined, insufficient funds, wrong OTP).
      rzp.on("payment.failed", (event) => {
        const desc = event?.error?.description || "Payment failed. Please try again with a different method.";
        toast.error(desc);
      });

      rzp.open();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Something went wrong.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, [onSuccess]);

  return { open, loading, config };
}

export default useRazorpayCheckout;
